import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { compareCustomerNames, customerDisplayName, customerMatches, CUSTOMER_DIRECTORY_COLUMNS, loadWorkshopRows } from '../lib/customerDirectory'
import './CustomerCombobox.css'

export default function CustomerCombobox(props) {
  return <ScopedCustomerCombobox key={props.workshopId} {...props} />
}

function ScopedCustomerCombobox({ workshopId, value, selectedCustomer, onChange, disabled = false }) {
  const id = useId()
  const inputRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [rows, setRows] = useState([])
  const [status, setStatus] = useState('idle')
  const [retry, setRetry] = useState(0)
  const [active, setActive] = useState(-1)
  const [limit, setLimit] = useState(50)
  const requested = status !== 'idle'
  useEffect(() => {
    if (!requested || !workshopId) return undefined
    const controller = new AbortController()
    setStatus('loading')
    loadWorkshopRows(supabase, 'customers', workshopId, CUSTOMER_DIRECTORY_COLUMNS, controller.signal)
      .then(data => { if (!controller.signal.aborted) { setRows(data.sort(compareCustomerNames)); setStatus('ready') } })
      .catch(() => { if (!controller.signal.aborted) setStatus('error') })
    return () => controller.abort()
  }, [workshopId, requested, retry])
  const matches = useMemo(() => rows.filter(row => customerMatches(row, query)), [rows, query])
  const visible = matches.slice(0, limit)
  const selected = rows.find(row => row.id === value) || (selectedCustomer?.id === value ? selectedCustomer : null)
  const optionId = row => `${id}-customer-${row.id}`
  useEffect(() => {
    if (open && active >= 0) document.getElementById(optionId(visible[active] || {}))?.scrollIntoView?.({ block: 'nearest' })
  })
  function show() {
    setOpen(true)
    if (status === 'idle') setStatus('loading')
  }
  function choose(row) {
    onChange(row?.id || '', row || null)
    setQuery('')
    setActive(-1)
    inputRef.current?.focus()
    setOpen(false)
  }
  return <div className="customer-combobox" onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget)) { setOpen(false); setQuery(''); setActive(-1) }
  }}>
    <label htmlFor={id}>Customer</label>
    <div className="customer-combobox-input">
      <input id={id} ref={inputRef} role="combobox" autoComplete="off"
        aria-autocomplete="list" aria-expanded={open} aria-controls={`${id}-list`}
        aria-activedescendant={open && status === 'ready' && visible[active] ? optionId(visible[active]) : undefined}
        aria-describedby={`${id}-help`} disabled={disabled || !workshopId}
        value={open ? query : selected ? customerDisplayName(selected) : ''}
        placeholder="Search name, email or phone…" onFocus={show} onClick={show}
        onChange={event => { setQuery(event.target.value); setActive(-1); setLimit(50); show() }}
        onKeyDown={event => {
          if (event.key === 'Escape') { setOpen(false); setQuery(''); setActive(-1); event.stopPropagation() }
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault(); show()
            setActive(current => event.key === 'ArrowDown' ? Math.min(current + 1, visible.length - 1) : Math.max(0, current - 1))
          }
          if (event.key === 'Enter' && open) { event.preventDefault(); if (visible[active]) choose(visible[active]) }
        }} />
      {value && <button type="button" className="btn btn-ghost btn-sm" aria-label="Clear customer selection" disabled={disabled} onClick={() => choose(null)}>Clear</button>}
    </div>
    <div id={`${id}-help`} className="text-muted fs12" aria-live="polite">
      {selected ? `Selected: ${customerDisplayName(selected)} · ${[selected.email, selected.phone].filter(Boolean).join(' · ') || 'No contact details'}` : 'Select a saved customer.'}
    </div>
    {open && <div className="customer-combobox-popup">
      <div role="status">{status === 'loading' ? 'Loading customers…' : status === 'error' ? 'Could not load customers.' : status === 'ready' && !matches.length ? 'No matching customers.' : ''}</div>
      {status === 'error' && <button type="button" className="btn btn-ghost" onClick={() => setRetry(n => n + 1)}>Retry customers</button>}
      <ul id={`${id}-list`} role="listbox" aria-label="Customers" aria-busy={status === 'loading'}>
        {status === 'ready' && visible.map((row, index) => <li key={row.id} id={optionId(row)} role="option" aria-selected={row.id === value}
          className={index === active ? 'active' : ''} onPointerDown={event => { event.preventDefault() }}
          onClick={() => choose(row)}>
          <strong>{customerDisplayName(row)}</strong><span>{[row.email, row.phone].filter(Boolean).join(' · ') || 'No contact details'}</span>
        </li>)}
      </ul>
      {status === 'ready' && matches.length > limit && <button type="button" className="btn btn-ghost" onClick={() => setLimit(n => n + 50)}>Show more ({visible.length} of {matches.length})</button>}
    </div>}
  </div>
}
